import AddIcon from "@mui/icons-material/Add";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import MenuIcon from "@mui/icons-material/Menu";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import {
  Alert,
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  keyframes,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
} from "@mui/material";
import axios from "axios";
import { endOfMonth, format, parseISO } from "date-fns";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Area,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import {
  createTreatmentProjection,
  deleteTreatmentProjection,
  fetchTreatmentProjections,
  getCustomerMonthlyTargets,
  getCustomTargetPerformanceData,
  getTargetCycle,
  getTargetValues,
  saveCustomMonthlyTarget,
  updateTargetCycle,
  updateTargetValue,
  updateTreatmentProjection,
} from "../../api/customerCustomTargets";
import { fallbackDate } from "../../config/dashboardDefaults";
import { useCurrentProfile } from "../../ContextWrapper";
import { customCurrencyFormat } from "../../utils";
import ClientDashboardLayout from "./dashboardLayout";

const formatMoney = (v) => customCurrencyFormat(Math.ceil(v));

const money = (n) =>
  n?.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });

const formatNumberWithCommas = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const num = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(num)) return "";
  return num.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

const formatNumberWithCommasPreserveDecimal = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const str = String(value);
  // If the string ends with a decimal point or has trailing zeros after decimal, preserve it
  if (str.endsWith(".") || /\.\d*0+$/.test(str)) {
    // Split into integer and decimal parts
    const parts = str.split(".");
    const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return parts.length > 1 ? integerPart + "." + parts[1] : integerPart + ".";
  }
  // Check if the value has a decimal part
  if (str.includes(".")) {
    const parts = str.split(".");
    const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    const decimalPart = parts[1] || "";
    // Preserve trailing zeros if they exist
    return integerPart + "." + decimalPart;
  }
  // Otherwise, use the standard formatting
  const num = parseFloat(str);
  if (isNaN(num)) return "";
  return num.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

const ColorDot = ({ color }) => (
  <Box
    sx={{
      width: 12,
      height: 12,
      borderRadius: "50%",
      backgroundColor: color,
      flexShrink: 0,
    }}
  />
);

const ChangeConfirmationModal = ({
  open,
  title = "Confirm Changes",
  description = "",
  changes = [],
  onCancel,
  onConfirm,
  valueType = "percentage",
}) => {
  const formatValue = (value) => {
    if (valueType === "currency") {
      return `$${formatNumberWithCommas(value)}`;
    }
    return `${formatNumberWithCommas(value)}%`;
  };

  const formatChange = (change) => {
    if (valueType === "currency") {
      return `${change >= 0 ? "+" : ""}${formatNumberWithCommas(change)}`;
    }
    return `${change >= 0 ? "+" : ""}${formatNumberWithCommas(change)}%`;
  };
  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2, p: 0, maxWidth: "680px" } }}
    >
      <DialogTitle sx={{ fontWeight: 600, fontSize: "18px", pb: 0 }}>
        {title}
      </DialogTitle>

      {description && (
        <Typography sx={{ px: 3, fontSize: "14px", color: "text.secondary" }}>
          {description}
        </Typography>
      )}
      <Divider sx={{ mt: 2, mb: 0 }}></Divider>

      <DialogContent sx={{ px: "24px", py: "32px" }}>
        <TableContainer
          component={Paper}
          sx={{
            borderRadius: "16px",
            boxShadow: "none",
            border: "1px solid rgba(0, 0, 0, 0.08)",
          }}
        >
          <Table>
            <TableHead>
              <TableRow
                sx={{
                  backgroundColor: "#1A202C",
                }}
              >
                {["Bucket", "Previous", "", "New", "Change"].map((head) => (
                  <TableCell
                    key={head}
                    sx={{
                      color: "#fff",
                      fontWeight: 600,
                      fontSize: "13px",
                      fontFamily: "lato",
                      px: head === "Bucket" ? "16px" : "0px",
                      borderBottom: "none",
                      textAlign: head === "Bucket" ? "left" : "center",
                      width: head === "Bucket" ? "50%" : "auto",
                    }}
                  >
                    {head}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {changes.map((row, i) => {
                const change = row.newValue - row.previous;
                const isPositive = change >= 0;

                return (
                  <TableRow key={i}>
                    <TableCell sx={{ width: "50%", fontWeight: "600" }}>
                      {row.label}
                    </TableCell>

                    <TableCell
                      align="center"
                      sx={{ color: "rgba(113, 128, 150, 1)", fontWeight: 600 }}
                    >
                      {formatValue(row.previous)}
                    </TableCell>

                    <TableCell
                      sx={{
                        width: "10px",
                        p: 0,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          height: "100%",
                        }}
                      >
                        <ArrowForwardIcon
                          sx={{ fontSize: 18, color: "rgba(113, 128, 150, 1)" }}
                        />
                      </Box>
                    </TableCell>

                    <TableCell align="center" sx={{ fontWeight: 600 }}>
                      {formatValue(row.newValue)}
                    </TableCell>

                    <TableCell align="center">
                      <Box
                        sx={{
                          borderRadius: "40px",
                          backgroundColor: isPositive
                            ? "rgba(198, 246, 213, 1)"
                            : "rgba(254, 215, 215, 1)",
                          color: isPositive
                            ? "rgba(37, 133, 90, 1)"
                            : "rgba(229, 62, 62, 1)",
                          fontSize: "12px",
                          fontWeight: 600,
                          height: "28px",
                          maxWidth: valueType === "currency" ? "100px" : "63px",
                          px: valueType === "currency" ? 1 : 0,

                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {formatChange(change)}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>

      <Divider sx={{ mt: 0, mb: 0 }}></Divider>

      <DialogActions sx={{ px: "24px", py: "20px" }}>
        <Button
          variant="outlined"
          sx={{
            height: 40,
            width: "100%",
            maxWidth: 80,
            fontFamily: "lato",
            borderRadius: "6px",
          }}
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          sx={{
            height: 40,
            width: "100%",
            maxWidth: 154,
            fontFamily: "lato",
            borderRadius: "6px",
          }}
          onClick={onConfirm}
        >
          Confirm Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export function SuccessTargetModal({
  open,
  onClose,
  monthlyTarget,
  profitGoal,
}) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderRadius: "14px",
          p: 4,
          maxWidth: 480,
          width: "100%",
          textAlign: "center",
        },
      }}
      BackdropProps={{
        sx: { backgroundColor: "rgba(0,0,0,0.45)" },
      }}
    >
      {/* Check Icon */}
      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <Box
          sx={{
            width: 142,
            height: 142,
            display: "flex",
          }}
        >
          <Box
            component="img"
            src="/custom-target-success.svg"
            alt=""
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "contain", // or "contain" depending on intent
              display: "block",
            }}
          />
        </Box>
      </Box>

      {/* Title */}
      <Typography
        sx={{
          fontSize: 22,
          fontWeight: 600,
          fontFamily: "lato",
          mb: 3,
        }}
      >
        Your Income Target Has Been Saved!
      </Typography>

      {/* Monthly Target Card */}
      <Paper
        elevation={0}
        sx={{
          border: "1px solid #627CFB",
          borderRadius: "10px",
          p: 2.2,
          mb: 2,
          backgroundColor: "#627CFB14",
        }}
      >
        <Typography
          sx={{
            fontSize: 14,
            fontWeight: 500,
            color: "#4A5568",
            mb: 0.5,
            fontFamily: "lato",
          }}
        >
          Monthly Target
        </Typography>
        <Typography
          sx={{
            fontSize: 28,
            fontWeight: 700,
            fontFamily: "lato",
          }}
        >
          {monthlyTarget}
        </Typography>
      </Paper>

      {/* Profit Goal Card */}
      <Paper
        elevation={0}
        sx={{
          border: "1px solid #48BB78",
          borderRadius: "10px",
          p: 2.2,
          mb: 3,
          backgroundColor: "#68D3911A",
        }}
      >
        <Typography
          sx={{
            fontSize: 14,
            fontWeight: 500,
            color: "#4A5568",
            mb: 0.5,
            fontFamily: "lato",
          }}
        >
          Profit Goal
        </Typography>
        <Typography
          sx={{
            fontSize: 28,
            fontWeight: 700,
            color: "#38A169",
            fontFamily: "lato",
          }}
        >
          {profitGoal}
        </Typography>
      </Paper>

      {/* Subtitle / encouragement */}
      <Typography
        sx={{
          fontSize: 15,
          color: "#4A5568",
          fontFamily: "lato",
          mb: 3,
        }}
      >
        You're all set! Time to crush those goals. 💪
      </Typography>

      {/* CTA */}
      <Button
        variant="contained"
        onClick={onClose}
        sx={{
          width: "100%",
          borderRadius: "10px",
          fontSize: 16,
          fontWeight: 600,
          py: 1.2,
          backgroundColor: "#627CFB",
          textTransform: "none",

          // Hover
          "&:hover": {
            backgroundColor: "#5166E0",
            boxShadow: "0px 6px 16px rgba(98, 124, 251, 0.35)",
          },

          "&:active": {
            backgroundColor: "#485BD6",
            transform: "scale(0.98)",
            boxShadow: "0px 3px 8px rgba(98, 124, 251, 0.35)",
          },

          "&.Mui-focusVisible": {
            outline: "2px solid #97B1FF",
            outlineOffset: "2px",
          },

          transition: "all 0.15s ease",
        }}
      >
        Let’s Go!
      </Button>
    </Dialog>
  );
}

export function TreatmentCalculatorUI({
  open,
  onClose,
  customerId,
  setTargetValue,
  setLastUpdated,
  endDate,
  indicatorId,
}) {
  useEffect(() => {
    if (!open || !customerId) return;

    fetchTreatmentProjections(customerId).then((res) => {
      const mapped = res.map((t) => ({
        id: t.id,
        name: t.service_name,
        fee: Number(t.average_fee),
        volume: Number(t.expected_volume),
        period: t.time_period,
        amount: Math.ceil(Number(t.revenue_estimate)),
      }));

      setTreatments(mapped);
      setActiveId(mapped[0]?.id ?? null);
    });
  }, [open, customerId]);

  const [treatments, setTreatments] = React.useState([]);

  const [draftTreatment, setDraftTreatment] = useState(null);

  const [activeId, setActiveId] = React.useState(1);

  const [isEditing, setIsEditing] = React.useState(false);

  const activeTreatment = treatments.find((t) => t.id === activeId);

  const handleAddTreatment = async () => {
    if (!customerId) return;

    const res = await createTreatmentProjection({
      customer: customerId,
      service_name: "New Treatment",
      average_fee: 0,
      expected_volume: 0,
      time_period: "monthly",
      revenue_estimate: 0,
      in_use: false,
    });

    const newTreatment = {
      id: res.data.id,
      name: res.data.service_name,
      fee: 0,
      volume: 0,
      period: "monthly",
      amount: 0,
    };

    setTreatments((prev) => [...prev, newTreatment]);

    setActiveId(newTreatment.id);
    setDraftTreatment({ ...newTreatment });
    setIsEditing(true);
  };

  const handleDeleteTreatment = async () => {
    if (!activeId) return;

    await deleteTreatmentProjection(activeId);

    setTreatments((prev) => {
      const filtered = prev.filter((t) => t.id !== activeId);

      const next = filtered[0] ?? null;

      setActiveId(next?.id ?? null);
      setDraftTreatment(null);
      setIsEditing(false);

      return filtered;
    });
  };

  const updateDraft = (field, value) => {
    setDraftTreatment((prev) => {
      const updated = { ...prev, [field]: value };

      let factor = 1;
      if (updated.period === "weekly") factor = 4.33;
      if (updated.period === "daily") factor = 30;

      updated.amount = Math.ceil(updated.fee * updated.volume * factor);

      return updated;
    });
  };

  const handleClose = () => {
    setIsEditing(false);
    setDraftTreatment(null);
    onClose();
    setTreatments([]);
  };

  const projectedTotal = React.useMemo(() => {
    return Math.ceil(treatments.reduce((sum, t) => sum + (t.amount || 0), 0));
  }, [treatments]);

  const treatment = isEditing ? draftTreatment : activeTreatment;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
          overflow: "hidden",
          maxHeight: { xs: "90vh", sm: "686px" },
        },
      }}
    >
      <DialogContent sx={{ p: 0, bgcolor: "#F5F5F7" }}>
        <Box
          sx={{
            p: "24px 20px 20px",
            background: "white",
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid #E0E0E0",
          }}
        >
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                p: "12px",
                borderRadius: "8px",
                background:
                  "linear-gradient(180deg, #536DEC 0%, #171923 123.75%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "white",
                fontSize: "26px",
              }}
            >
              <Box
                component="img"
                src={"/white-calculator.svg"}
                alt=""
                sx={{ width: 24, height: 24 }}
              />
            </Box>
            <Box>
              <Typography
                sx={{ fontFamily: "lato", fontSize: 22, fontWeight: 700 }}
              >
                Treatment Projection Tool
              </Typography>
              <Typography
                sx={{
                  fontFamily: "lato",
                  fontSize: 14,
                  color: "text.secondary",
                }}
              >
                Build your target from treatment volumes
              </Typography>
            </Box>
          </Box>
          <Box
            sx={{
              textAlign: { xs: "left", sm: "right" },
              flexGrow: { xs: 1, sm: 0 },
            }}
          >
            <Typography
              sx={{ fontFamily: "lato", color: "text.secondary", fontSize: 14 }}
            >
              Projected Total
            </Typography>
            <Typography
              sx={{
                fontFamily: "lato",
                fontWeight: 700,
                fontSize: { xs: 20, sm: 24 },
              }}
            >
              {formatMoney(Number(projectedTotal))}
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "380px 1fr" },
            height: { xs: "auto", md: "700px" },
            maxHeight: { xs: "none", md: "509px" },
          }}
        >
          <Box
            sx={{
              background: "white",
              borderRight: { xs: "none", md: "1px solid #E0E0E0" },
              borderBottom: { xs: "1px solid #E0E0E0", md: "none" },
              maxHeight: { xs: "calc(90vh - 200px)", md: "509px" },
              overflowY: "none",
            }}
          >
            <Box
              sx={{
                p: "16px 24px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography
                sx={{ fontFamily: "lato", fontWeight: 600, fontSize: 16 }}
              >
                Revenue Drivers
              </Typography>
              <Typography
                sx={{
                  fontFamily: "lato",
                  color: "text.secondary",
                  fontSize: 14,
                }}
              >
                {treatments.length} total
              </Typography>
            </Box>
            <Divider />
            <Box
              sx={{
                p: "0px 24px 16px",
                bgcolor: "#F5F5F7",
                maxHeight: { xs: "381px", sm: "381px" },
                overflowY: "auto",
                minHeight: { xs: "381px", sm: "381px" },
              }}
            >
              {treatments.length === 0 ? (
                <Box
                  sx={{
                    my: { xs: 4, sm: "140px" },
                    px: "24px",
                    textAlign: "center",
                    color: "text.secondary",
                  }}
                >
                  <Typography
                    sx={{ fontFamily: "lato", fontSize: 16, fontWeight: 600 }}
                  >
                    No revenue drivers yet
                  </Typography>
                  <Typography sx={{ fontFamily: "lato", fontSize: 14, mt: 1 }}>
                    Add a treatment to start building your target
                  </Typography>
                </Box>
              ) : (
                treatments.map((t) => {
                  const isActive = t.id === activeId;
                  return (
                    <Box
                      key={t.id}
                      onClick={() => {
                        setActiveId(t.id);
                        setIsEditing(false);
                        setDraftTreatment(null);
                      }}
                      sx={{
                        border: isActive
                          ? "2px solid #536DEC"
                          : "1px solid #E0E0E0",
                        borderRadius: "8px",
                        p: "18px",
                        height: "124px",
                        mt: "16px",
                        background: "#FFFFFF",
                        cursor: isActive ? "default" : "pointer",
                        transition: "all 0.15s ease",
                        "&:hover": { borderColor: "#536DEC" },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Typography
                          sx={{
                            fontFamily: "lato",
                            fontSize: 16,
                            fontWeight: 600,
                            color: isActive ? "#627CFB" : "#171923",
                          }}
                        >
                          {t.name}
                        </Typography>
                        {isActive && (
                          <EditIcon sx={{ fontSize: 18, color: "#627CFB" }} />
                        )}
                      </Box>
                      <Typography
                        sx={{
                          fontFamily: "lato",
                          fontWeight: 700,
                          fontSize: 24,
                        }}
                      >
                        $
                        {t?.amount?.toLocaleString(undefined, {
                          maximumFractionDigits: 0,
                        })}
                        <Typography
                          component="span"
                          sx={{
                            fontFamily: "lato",
                            fontSize: 16,
                            color: "text.secondary",
                            ml: 1,
                          }}
                        >
                          / month
                        </Typography>
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: "lato",
                          fontSize: 13,
                          color: "text.secondary",
                          mt: "4px",
                        }}
                      >
                        ${formatNumberWithCommas(Math.ceil(t.fee))} × {formatNumberWithCommas(t.volume)} • {t.period}
                      </Typography>
                    </Box>
                  );
                })
              )}
            </Box>
            <Divider />
            <Box sx={{ p: "16px 24px" }}>
              <Paper
                onClick={handleAddTreatment}
                sx={{
                  elevation: 0,
                  boxShadow: "none",
                  p: "7px 24px",
                  borderRadius: "8px",
                  border: "2px dashed #D1D5DC",
                  textAlign: "center",
                  cursor: "pointer",
                  height: "40px",
                  "&:hover": {
                    borderColor: "#4F64E9",
                    bgcolor: "#627CFB0F",
                    color: "#4F64E9",
                  },

                  "&:active": {
                    borderColor: "#4F64E9",
                    bgcolor: "#627CFB1F",
                    color: "#4F64E9",
                  },
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "lato",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    justifyContent: "center",
                  }}
                >
                  <AddIcon fontSize="small" /> Add Treatment
                </Typography>
              </Paper>
            </Box>
          </Box>

          <Box
            sx={{
              p: { xs: "16px 20px", md: "20px 20px 32px 32px" },
              overflowY: { xs: "visible", md: "auto" },
              maxHeight: { xs: "none", md: "509px" },
              bgcolor: "white",
            }}
          >
            {activeTreatment && (
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    sx={{ fontFamily: "lato", fontSize: 20, fontWeight: 700 }}
                  >
                    Treatment Details
                  </Typography>
                  <Box
                    sx={{
                      marginLeft: "auto",
                      display: "flex",
                      gap: 1,
                      flexWrap: { xs: "wrap", sm: "nowrap" },
                      width: { xs: "100%", sm: "auto" },
                      mt: { xs: 2, sm: 0 },
                    }}
                  >
                    <Button
                      variant="contained"
                      sx={{
                        fontFamily: "lato",
                        borderRadius: "8px",
                        bgcolor: "#627CFB",
                      }}
                      startIcon={isEditing ? <CheckIcon /> : <EditIcon />}
                      onClick={async () => {
                        if (!activeTreatment) return;
                        if (isEditing) {
                          await updateTreatmentProjection(activeTreatment.id, {
                            service_name: draftTreatment.name,
                            average_fee: draftTreatment.fee,
                            expected_volume: draftTreatment.volume,
                            time_period: draftTreatment.period,
                            revenue_estimate: draftTreatment.amount,
                          });
                          setTreatments((prev) =>
                            prev.map((t) =>
                              t.id === activeTreatment.id ? draftTreatment : t,
                            ),
                          );
                          setIsEditing(false);
                          setDraftTreatment(null);
                        } else {
                          setDraftTreatment({ ...activeTreatment });
                          setIsEditing(true);
                        }
                      }}
                    >
                      {isEditing ? "Save" : "Edit"}
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      sx={{ fontFamily: "lato", borderRadius: "8px" }}
                      startIcon={<DeleteIcon />}
                      onClick={handleDeleteTreatment}
                    >
                      Delete
                    </Button>
                  </Box>
                </Box>

                <Box sx={{ mt: "24px" }}>
                  <Typography sx={{ fontFamily: "lato", fontSize: 14, mb: 1 }}>
                    Treatment / Service Name
                  </Typography>
                  <TextField
                    slotProps={{
                      input: {
                        sx: {
                          fontFamily: "lato",
                          height: "44px",
                          borderRadius: "8px",
                        },
                      },
                    }}
                    fullWidth
                    value={treatment.name}
                    onChange={(e) => updateDraft("name", e.target.value)}
                    disabled={!isEditing}
                  />
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    gap: 3,
                    mt: "16px",
                    flexDirection: { xs: "column", sm: "row" },
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      sx={{ fontSize: 14, fontFamily: "lato", mb: 1 }}
                    >
                      Average Fee per Treatment
                    </Typography>
                    <TextField
                      slotProps={{
                        input: {
                          startAdornment: (
                            <InputAdornment position="start">$</InputAdornment>
                          ),
                          sx: {
                            height: "44px",
                            fontFamily: "lato",
                            borderRadius: "8px",
                            textAlign: "right",
                          },
                        },
                      }}
                      disabled={!isEditing}
                      fullWidth
                      type="text"
                      inputMode="decimal"
                      value={
                        treatment.fee === null
                          ? ""
                          : formatNumberWithCommasPreserveDecimal(treatment.fee)
                      }
                      onChange={(e) => {
                        const val = e.target.value.replace(/,/g, "");

                        if (val === "") {
                          updateDraft("fee", null);
                          return;
                        }

                        if (/^\d*\.?\d{0,2}$/.test(val)) {
                          updateDraft("fee", val);
                        }
                      }}
                      onBlur={() => {
                        if (treatment.fee === "" || treatment.fee == null) {
                          updateDraft("fee", 0);
                        } else {
                          updateDraft("fee", Number(treatment.fee));
                        }
                      }}
                    />
                  </Box>

                  <Box sx={{ flex: 1 }}>
                    <Typography
                      sx={{ fontSize: 14, fontFamily: "lato", mb: 1 }}
                    >
                      Expected Volume
                    </Typography>
                    <TextField
                      slotProps={{
                        input: {
                          sx: {
                            fontFamily: "lato",
                            height: "44px",
                            borderRadius: "8px",
                            textAlign: "right",
                          },
                        },
                      }}
                      fullWidth
                      type="text"
                      inputMode="decimal"
                      value={
                        treatment.volume === null
                          ? ""
                          : formatNumberWithCommasPreserveDecimal(treatment.volume)
                      }
                      onChange={(e) => {
                        const raw = e.target.value.replace(/,/g, "");
                        if (raw === "") {
                          updateDraft("volume", null);
                          return;
                        }
                        if (/^\d*\.?\d{0,2}$/.test(raw)) {
                          updateDraft("volume", raw);
                        }
                      }}
                      onBlur={() => {
                        if (
                          treatment.volume === "" ||
                          treatment.volume === null ||
                          treatment.volume === undefined
                        ) {
                          updateDraft("volume", 0);
                        } else {
                          updateDraft("volume", Number(treatment.volume));
                        }
                      }}
                      disabled={!isEditing}
                    />
                  </Box>
                </Box>

                <Box sx={{ mt: "16px" }}>
                  <Typography sx={{ fontSize: 14, fontFamily: "lato", mb: 1 }}>
                    Time Period
                  </Typography>
                  <Select
                    sx={{
                      height: "44px",
                      borderRadius: "8px",
                      fontFamily: "lato",
                      "& .MuiSelect-select": {
                        fontFamily: "lato",
                        height: "44px",
                        display: "flex",
                        alignItems: "center",
                        padding: "0 14px",
                      },
                    }}
                    disabled={!isEditing}
                    fullWidth
                    value={treatment.period}
                    onChange={(e) => updateDraft("period", e.target.value)}
                  >
                    <MenuItem value="monthly">Monthly</MenuItem>
                    <MenuItem value="weekly">Weekly</MenuItem>
                    <MenuItem value="daily">Daily</MenuItem>
                  </Select>
                </Box>

                <Box
                  sx={{
                    mt: "24px",
                    p: "20px",
                    minHeight: "108px",
                    borderRadius: "8px",
                    background: "#F2F5FF",
                    border: "2px solid #A9BBFF",
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "lato",
                      fontSize: 14,
                      color: "#536DEC",
                      fontWeight: 600,
                    }}
                  >
                    Monthly Revenue from this treatment
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: "lato",
                      fontWeight: 700,
                      fontSize: 32,
                      mt: 1,
                    }}
                  >
                    $
                    {treatment?.amount?.toLocaleString(undefined, {
                      maximumFractionDigits: 0,
                    })}
                    <Typography
                      component="span"
                      sx={{
                        fontSize: 16,
                        color: "text.secondary",
                        fontFamily: "lato",
                        ml: 1,
                      }}
                    >
                      / month
                    </Typography>
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        </Box>

        {/* Footer */}
        <Divider />
        <Box
          sx={{
            display: "flex",
            justifyContent: { xs: "space-between", sm: "flex-end" },
            gap: 2,
            bgcolor: "white",
            p: "16px 20px",
            flexWrap: "nowrap",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: "center",
          }}
        >
          <Button
            variant="outlined"
            sx={{
              fontFamily: "lato",
              borderRadius: "8px",
              height: "40px",
              fontSize: "16px",
              fontWeight: 600,
              width: { xs: "100%", sm: "auto" },
            }}
            onClick={handleClose}
          >
            Close
          </Button>
          <Button
            variant="contained"
            sx={{
              fontFamily: "lato",
              bgcolor: "#536DEC",
              borderRadius: "8px",
              height: "40px",
              fontSize: "16px",
              fontWeight: 600,
              width: { xs: "100%", sm: "auto" },
              mt: { xs: 1, sm: 0 },
            }}
            onClick={async () => {
              const value = Math.ceil(projectedTotal);
              handleClose();
              setLastUpdated?.(new Date());

              if (customerId && endDate && indicatorId) {
                try {
                  await updateTargetValue({
                    customerId,
                    endDate,
                    indicatorId,
                    targetValue: value,
                  });
                  setTargetValue(value);
                } catch (err) {
                  console.error("Failed to save target value:", err);
                }
              }
            }}
          >
            Use As Target
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

export function MonthlyTargetModal({
  open,
  onClose,
  customerId,
  targetValue,
  previousTargetValue,
  setTargetValue,
  formattedDate,
  setNextTargetValue,
  setLastUpdated,
}) {
  const [rows, setRows] = useState([]);
  const [editIndex, setEditIndex] = useState(null);

  const [growth, setGrowth] = useState(3);
  const [growthInput, setGrowthInput] = useState("3");

  const [draftRows, setDraftRows] = useState(null);

  const prevGrowthRef = useRef(growth);

  const toggleEdit = (idx) => {
    setDraftRows(JSON.parse(JSON.stringify(rows)));
    setEditIndex(idx);
  };

  const cancelEdit = () => {
    if (draftRows) {
      setRows(draftRows);
    }
    setDraftRows(null);
    setEditIndex(null);
  };

  const updateMode = (idx, mode) => {
    setRows((prev) => {
      const next = [...prev];
      next[idx] = {
        ...next[idx],
        mode: mode || "fixed",
        input: null,
      };
      return next;
    });
  };

  const updateInput = (idx, value) => {
    setRows((prev) =>
      prev.map((r, i) =>
        i === idx ? { ...r, input: value === "" ? null : value } : r,
      ),
    );
  };

  const recalcRows = useCallback(
    (rows, targetValue, growth, previousTargetValue) => {
      if (!rows.length) return rows;

      const growthRate = Number(growth || 0) / 100;
      let prev = Number(previousTargetValue ?? targetValue ?? 0);

      return rows.map((r, index) => {
        let value = prev;

        if (index === 0) {
          if (r.mode === "fixed" && r.input != null) {
            value = Number(r.input);
          } else if (r.mode === "percentage" && r.input != null) {
            value = prev * (1 + Number(r.input) / 100);
          }
        } else {
          if (r.mode === "fixed" && r.input != null) {
            value = Number(r.input);
          } else if (r.mode === "percentage" && r.input != null) {
            value = prev * (1 + Number(r.input) / 100);
          } else {
            value = prev * (1 + growthRate);
          }
        }

        value = Math.round(value * 100) / 100;
        prev = value;

        return { ...r, value };
      });
    },
    [],
  );

  const applyEdit = () => {
    if (editIndex === null) return;
    setRows((prev) =>
      recalcRows(prev, targetValue, growth, previousTargetValue),
    );
    setEditIndex(null);
  };

  const buildPayload = () => {
    const annual = rows.reduce((s, r) => s + Number(r.value || 0), 0);
    const avg = annual / 12;

    return {
      growth_pct: growth,
      per_month_average: avg,
      projected_annual_total: annual,
      months: rows.map((r) => ({
        month: r.monthNum,
        mode: r.mode,
        pct_value:
          r.mode === "percentage"
            ? r.input !== null
              ? Number(r.input)
              : null
            : null,
        fixed_value:
          r.mode === "fixed"
            ? r.input !== null
              ? Number(r.input)
              : null
            : null,
        projected_value: Number(r.value),
      })),
    };
  };

  const adjustInput = (idx, delta) => {
    setRows((prev) =>
      prev.map((r, i) => {
        if (i !== idx) return r;

        const step = r.mode === "percentage" ? 1 : 100;
        const current = Number(
          r.input ?? (r.mode === "percentage" ? growth : r.value) ?? 0,
        );

        return { ...r, input: Math.max(0, current + delta * step) };
      }),
    );
  };

  useEffect(() => {
    setRows((prev) => {
      if (!prev.length) return prev;

      const baseRows = prev;

      const next = recalcRows(
        baseRows,
        targetValue,
        growth,
        previousTargetValue,
      );

      prevGrowthRef.current = growth;

      return next;
    });
  }, [targetValue, growth, previousTargetValue, recalcRows]);

  useEffect(() => {
    if (!open || !customerId) return;

    getTargetCycle(customerId, formattedDate).then((data) => {
      const apiGrowth = data.growth_pct ?? 0;
      setGrowth(apiGrowth);
      setGrowthInput(String(apiGrowth));

      const baseMonths = [
        { short: "Jan", long: "January" },
        { short: "Feb", long: "February" },
        { short: "Mar", long: "March" },
        { short: "Apr", long: "April" },
        { short: "May", long: "May" },
        { short: "Jun", long: "June" },
        { short: "Jul", long: "July" },
        { short: "Aug", long: "August" },
        { short: "Sep", long: "September" },
        { short: "Oct", long: "October" },
        { short: "Nov", long: "November" },
        { short: "Dec", long: "December" },
      ];

      const start = new Date(formattedDate).getMonth();
      const rotated = Array.from({ length: 12 }, (_, i) => {
        const idx = (start + i) % 12;
        return { ...baseMonths[idx], monthNum: idx + 1 };
      });

      const apiMonths = data.months || data.target_cycle_months || [];

      const monthMap = {};
      apiMonths.forEach((m) => {
        monthMap[m.month] = m;
      });

      const mapped = rotated.map((m) => {
        const monthData = monthMap[m.monthNum] || {};
        const mode = monthData.mode || "fixed";

        return {
          monthShort: m.short,
          monthLong: m.long,
          monthNum: m.monthNum,
          indicatorId: monthData.id,
          mode,
          input:
            mode === "percentage"
              ? (monthData.pct_value ?? null)
              : (monthData.fixed_value ?? null),
          value: monthData.projected_value
            ? Number(monthData.projected_value)
            : 0,
        };
      });

      setRows(recalcRows(mapped, targetValue, apiGrowth, previousTargetValue));
    });
  }, [
    open,
    customerId,
    formattedDate,
    targetValue,
    previousTargetValue,
    recalcRows,
  ]);

  const handleGrowthChange = (newGrowth) => {
    if (prevGrowthRef.current === newGrowth) return;

    setRows((prev) => {
      const cleared = prev.map((r, idx) => {
        if (idx === 0) return r;
        return { ...r, mode: "fixed", input: null };
      });

      prevGrowthRef.current = newGrowth;

      return recalcRows(cleared, targetValue, newGrowth, previousTargetValue);
    });

    setGrowth(newGrowth);
    setGrowthInput(String(newGrowth));
  };

  const starting = rows?.[0]?.value ?? 0;
  const ending = rows?.[rows.length - 1]?.value ?? 0;
  const avg = rows.reduce((s, r) => s + Number(r.value || 0), 0) / 12;
  const annual = rows.reduce((s, r) => s + Number(r.value || 0), 0);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { borderRadius: "12px", overflow: "hidden", maxWidth: 800 },
      }}
    >
      <DialogContent sx={{ p: 0, bgcolor: "#F5F5F7" }}>
        <Box
          sx={{
            p: { xs: "16px 12px", sm: "24px 20px 20px" },
            background: "white",
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            gap: 2,
            borderBottom: "1px solid #E0E0E0",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                p: "10px",
                borderRadius: 2,
                background:
                  "linear-gradient(180deg, #536DEC 0%, #171923 123.75%)",
              }}
            >
              <Box
                component="img"
                src="/white-target-icon.svg"
                alt=""
                sx={{ width: 20, height: 20 }}
              />
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography
                sx={{ fontFamily: "lato", fontWeight: 700, fontSize: 17 }}
              >
                Monthly Target Cycling
              </Typography>
              <Typography
                sx={{ fontFamily: "lato", fontSize: 14 }}
                color="text.secondary"
              >
                Set how your target changes month-over-month
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              maxHeight: { xs: "unset", md: "509px" },

              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                flex: { xs: "unset", md: "1 1 50%" },
                display: "flex",
                flexDirection: "column",
                p: { xs: 2, md: 3 },
                background: "white",
                minHeight: 0,
                borderRight: { xs: "none", md: "1px solid #E5E7EB" },
                borderBottom: { xs: "1px solid #E5E7EB", md: "none" },
              }}
            >
              <Typography sx={{ fontWeight: 600, fontSize: 16 }}>
                Monthly Growth Rate
              </Typography>
              <Typography sx={{ fontWeight: 400, fontSize: 14, mb: 1 }}>
                Choose how much your target should change each month
              </Typography>
              <TextField
                size="small"
                fullWidth
                value={formatNumberWithCommasPreserveDecimal(growthInput)}
                onChange={(e) => {
                  let val = e.target.value.replace(/,/g, "");

                  if (val === "" || val === "-") {
                    setGrowthInput(val);
                    return;
                  }

                  if (/^-?\d*(\.\d{0,2})?$/.test(val)) {
                    setGrowthInput(val);

                    const parsed = parseFloat(val);
                    if (!isNaN(parsed)) {
                      handleGrowthChange(parsed);
                    }
                  }
                }}
                placeholder="0"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">%</InputAdornment>
                  ),
                  sx: {
                    fontFamily: "lato",
                    width: "100%",
                    height: 48,
                    borderRadius: "8px",
                    borderWidth: "1px",
                    borderStyle: "solid",
                    borderColor: "#CBD5E0",
                    backgroundColor: "#F7FAFC",
                    paddingTop: "16px",
                    paddingBottom: "16px",
                    pl: "45%",
                    boxSizing: "border-box",
                    fontSize: "16px",
                    fontWeight: 600,
                    "& .MuiInputBase-input": {
                      height: "100%",
                      padding: 0,
                    },
                  },
                }}
              />
              <Typography sx={{ fontWeight: 400, fontSize: 14, mt: 2 }}>
                Quick Select:
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 0.5 }}>
                {[10, 5, 0, -5].map((v) => {
                  const isSelected = growth === v;
                  return (
                    <Paper
                      key={v}
                      elevation={0}
                      onClick={() => handleGrowthChange(v)} // ✅ call handler
                      sx={{
                        flex: "0 0 calc(50% - 4px)",
                        p: "10px 8px",
                        borderRadius: 2,
                        textAlign: "center",
                        cursor: "pointer",
                        border: isSelected
                          ? "2px solid #627CFB"
                          : "1px solid #E5E7EB",
                        bgcolor: isSelected ? "#F4F6FF" : "#FFFFFF",
                        fontWeight: 400,
                        fontSize: 14,
                        transition: "all 0.2s ease",
                        "&:hover": {
                          border: isSelected
                            ? "2px solid #627CFB"
                            : "1px solid #CBD5E1",
                          bgcolor: isSelected ? "#F4F6FF" : "#F9FAFB",
                        },
                      }}
                    >
                      {v > 0 ? `+${v}%` : v === 0 ? "Flat" : `${v}%`}
                    </Paper>
                  );
                })}
              </Box>

              <Box sx={{ flexGrow: 1, minHeight: 0 }} />
              <Box sx={{ mt: "auto", pt: 2 }}>
                <Divider sx={{ mb: 2 }} />
                <SummaryRow
                  label="Starting target"
                  value={customCurrencyFormat(Math.ceil(starting))}
                />
                <SummaryRow
                  label="Target in 12 months"
                  value={customCurrencyFormat(Math.ceil(ending))}
                />
                <SummaryRow
                  label="Avg per month"
                  value={customCurrencyFormat(Math.ceil(avg))}
                />
                <Divider sx={{ my: 2 }} />
                <SummaryRow
                  label="Annual total"
                  value={customCurrencyFormat(Math.ceil(annual))}
                  bold
                />
              </Box>
            </Box>

            <Box
              sx={{
                flex: { xs: "unset", md: "1 1 50%" },
                display: "flex",
                flexDirection: "column",
                minHeight: 0,
              }}
            >
              <Box
                sx={{
                  p: { xs: 2, md: 3 },
                  bgcolor: "white",
                  borderBottom: "1px solid #E0E0E0",
                  flexShrink: 0,
                }}
              >
                <Typography
                  sx={{ fontWeight: 600, fontSize: 16, fontFamily: "lato" }}
                >
                  12-Month Projection
                </Typography>
              </Box>

              <Box
                sx={{
                  flex: 1,
                  overflowY: { xs: "visible", md: "auto" },
                  p: { xs: 2, md: 3 },
                }}
              >
                <Stack spacing={2}>
                  {rows.map((r, idx) => {
                    const editing = editIndex === idx;
                    return (
                      <Paper
                        elevation={0}
                        key={idx}
                        sx={{
                          p: 2,
                          borderRadius: 3,
                          border: editing
                            ? "1px solid #536DEC"
                            : "1px solid #E0E0E0",
                        }}
                      >
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={2}
                          alignItems={editing ? "flex-start" : "center"}
                          justifyContent="space-between"
                        >
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: 2,
                              bgcolor: editing ? "#536DEC" : "#F3F4F6",
                              color: editing ? "white" : "black",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: 14,
                              flexShrink: 0,
                              mt: editing ? "2px" : 0,
                            }}
                          >
                            {r.monthShort}
                          </Box>

                          {!editing ? (
                            <Stack flex={1} spacing={0.5}>
                              <Typography
                                sx={{ fontSize: 15, fontWeight: 600 }}
                              >
                                {customCurrencyFormat(
                                  Math.ceil(r.value),
                                  false,
                                  false,
                                  true,
                                )}
                              </Typography>
                              <Typography
                                sx={{ fontSize: 14, color: "text.secondary" }}
                              >
                                {r.monthLong}
                              </Typography>
                            </Stack>
                          ) : (
                            <Stack flex={1} spacing={"10px"}>
                              <ToggleButtonGroup
                                exclusive
                                fullWidth
                                value={r.mode || "fixed"}
                                onChange={(e, val) =>
                                  updateMode(idx, val || "fixed")
                                }
                                sx={{
                                  borderRadius: "8px",
                                  backgroundColor: "#EEF1F4",
                                  padding: "2px",
                                  "& .MuiToggleButton-root": {
                                    flex: 1,
                                    borderRadius: "8px",
                                    margin: "2px 2px",
                                    fontSize: "13px",
                                    height: "28px",
                                    textTransform: "none",
                                    border: "none",
                                    color: "#4A5568",

                                    "&.Mui-selected": {
                                      backgroundColor: "#FFFFFF",
                                      color: "black",
                                      fontWeight: 500,
                                    },
                                  },
                                }}
                              >
                                <ToggleButton value="fixed">Fixed</ToggleButton>
                                <ToggleButton value="percentage">
                                  Percentage
                                </ToggleButton>
                              </ToggleButtonGroup>

                              <Box mt="10px" mb="10px">
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  alignItems="center"
                                >
                                  <IconButton
                                    size="small"
                                    onClick={() => adjustInput(idx, -1)}
                                    sx={{
                                      width: 32,
                                      height: 32,
                                      borderRadius: "8px",
                                      border: "1px solid",
                                      borderColor: "divider",
                                    }}
                                  >
                                    −
                                  </IconButton>

                                  <TextField
                                    size="small"
                                    fullWidth
                                    value={
                                      r.input !== null
                                        ? formatNumberWithCommasPreserveDecimal(r.input)
                                        : ""
                                    }
                                    placeholder={
                                      r.mode === "percentage"
                                        ? formatNumberWithCommas(growth)
                                        : formatNumberWithCommas(r.value)
                                    }
                                    onChange={(e) => {
                                      let val = e.target.value.replace(/,/g, "");

                                      if (val === "" || val === "-") {
                                        updateInput(idx, val);
                                        return;
                                      }

                                      if (/^-?\d*(\.\d{0,2})?$/.test(val)) {
                                        updateInput(idx, val);
                                      }
                                    }}
                                    inputProps={{
                                      inputMode: "decimal",
                                      pattern: "^\\d*(\\.\\d{0,2})?$",
                                      style: { textAlign: "center" },
                                    }}
                                    InputProps={{
                                      startAdornment:
                                        r.mode === "fixed" ? (
                                          <InputAdornment position="start">
                                            $
                                          </InputAdornment>
                                        ) : null,
                                      endAdornment:
                                        r.mode !== "fixed" ? (
                                          <InputAdornment position="end">
                                            %
                                          </InputAdornment>
                                        ) : null,
                                    }}
                                    sx={{ borderRadius: "8px" }}
                                  />

                                  <IconButton
                                    size="small"
                                    onClick={() => adjustInput(idx, 1)}
                                    sx={{
                                      width: 32,
                                      height: 32,
                                      borderRadius: "8px",
                                      border: "1px solid",
                                      borderColor: "divider",
                                    }}
                                  >
                                    +
                                  </IconButton>
                                </Stack>
                              </Box>

                              <Stack direction="row" spacing={1}>
                                <Button
                                  fullWidth
                                  variant="contained"
                                  onClick={applyEdit}
                                  sx={{
                                    height: 32,
                                    minWidth: 32,
                                    padding: 0,
                                    borderRadius: "8px",
                                  }}
                                >
                                  Save
                                </Button>
                                <Button
                                  variant="outlined"
                                  onClick={cancelEdit}
                                  sx={{
                                    width: 32,
                                    height: 32,
                                    minWidth: 32,
                                    padding: 0,
                                    borderRadius: "8px",
                                  }}
                                >
                                  <CloseIcon fontSize="small" />
                                </Button>
                              </Stack>
                            </Stack>
                          )}

                          {!editing && (
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={1}
                            >
                              {idx === 0 ? (
                                <>
                                  <Typography
                                    sx={{
                                      fontSize: 14,
                                      fontWeight: 600,
                                      color: "#536DEC",
                                    }}
                                  >
                                    Current Month
                                  </Typography>

                                  <IconButton
                                    size="small"
                                    onClick={() => toggleEdit(idx)}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </>
                              ) : (
                                <>
                                  {(() => {
                                    const prev = rows[idx - 1].value;
                                    const diff = r.value - prev;
                                    const pctDiff = prev
                                      ? (diff / prev) * 100
                                      : 0;
                                    const color =
                                      diff > 0
                                        ? "#38A169"
                                        : diff < 0
                                          ? "#E53E3E"
                                          : "gray";

                                    return (
                                      <Box
                                        display="flex"
                                        flexDirection="column"
                                        alignItems="center"
                                      >
                                        <Typography
                                          sx={{
                                            fontSize: 14,
                                            fontWeight: 600,
                                            color,
                                          }}
                                        >
                                          {customCurrencyFormat(
                                            Math.ceil(diff),
                                            false,
                                            false,
                                            false,
                                            false,
                                            false,
                                            true,
                                          )}
                                        </Typography>
                                        <Typography
                                          sx={{
                                            fontSize: 12,
                                            fontWeight: 500,
                                            color,
                                          }}
                                        >
                                          ({customCurrencyFormat(pctDiff, true)}
                                          )
                                        </Typography>
                                      </Box>
                                    );
                                  })()}

                                  <IconButton
                                    size="small"
                                    onClick={() => toggleEdit(idx)}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </>
                              )}
                            </Stack>
                          )}
                        </Stack>
                      </Paper>
                    );
                  })}
                </Stack>
              </Box>
            </Box>
          </Box>

          <Divider />
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
              p: 2,
              bgcolor: "white",
              flexShrink: 0,
            }}
          >
            <Button
              variant="outlined"
              onClick={() => {
                setRows([]);
                setGrowth(3);
                onClose();
              }}
              sx={{ borderRadius: 2, height: 40 }}
            >
              Close
            </Button>
            <Button
              variant="contained"
              sx={{ borderRadius: 2, height: 40 }}
              onClick={async () => {
                const payload = buildPayload();
                await updateTargetCycle(customerId, formattedDate, payload);

                if (setTargetValue && rows.length > 0) {
                  setTargetValue(Math.round(rows[0].value));
                  setNextTargetValue(Math.round(rows[1].value));
                  setLastUpdated?.(new Date());
                }

                onClose();
              }}
            >
              Save
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

const SummaryRow = ({ label, value, bold }) => (
  <Stack direction="row" justifyContent="space-between" my={1}>
    <Typography
      variant="body2"
      color="text.secondary"
      sx={{ fontFamily: "Lato", fontSize: "14px" }}
    >
      {label}
    </Typography>
    <Typography
      variant="body2"
      fontWeight={bold ? 700 : 600}
      sx={{
        fontFamily: "Lato",
        fontSize: bold ? "16px" : "14px",
        color: bold ? "#536DEC" : "black",
      }}
    >
      {value}
    </Typography>
  </Stack>
);

export const SegmentedBar = ({ segments = [], hoveredLabel, onHover }) => {
  const [tooltip, setTooltip] = React.useState({
    x: 0,
    y: 0,
    label: null,
  });

  const total = segments.reduce((sum, s) => sum + Number(s.value || 0), 0);
  const unused = Math.max(0, 100 - total);

  const unusedIsRightEnd = unused > 0;
  const unusedIsLeftEnd = total <= 0;

  const dashRotate = keyframes`
    0% { background-position: 0 0; }
    100% { background-position: 20px 0; }
  `;

  const handleEnter = (e, label) => {
    const rect = e.currentTarget.getBoundingClientRect();

    setTooltip({
      x: rect.left + rect.width / 2,
      y: rect.top,
      label,
    });

    onHover?.(label);
  };

  const handleLeave = () => {
    setTooltip((t) => ({ ...t, label: null }));
    onHover?.(null);
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          width: "100%",
          height: 48,
          borderRadius: 2,
          overflow: "hidden",
        }}
        onMouseLeave={handleLeave}
      >
        {segments.map((s, i) => {
          const isHovered = hoveredLabel === s.label;
          const isDimmed = hoveredLabel && !isHovered;

          return (
            <Box
              key={s.label + i}
              onMouseEnter={(e) => handleEnter(e, s.label)}
              sx={{
                fontFamily: "lato",
                width: `${s.value}%`,
                background: s.color,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
                opacity: isDimmed ? 0.35 : 1,
                transition:
                  "width 500ms ease, opacity 120ms ease, filter 120ms ease",
                "&:hover": {
                  filter: "brightness(1.1)",
                  opacity: 1,
                },
              }}
            >
              {s.value !== 0
                ? Number.isInteger(s.value)
                  ? `${s.value}%`
                  : `${s.value.toFixed(2)}%`
                : ""}
            </Box>
          );
        })}

        {unused > 0 && (
          <Box
            onMouseEnter={(e) => handleEnter(e, "Unallocated")}
            sx={{
              fontFamily: "lato",
              width: `${unused}%`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 12,
              fontWeight: 600,
              color: "#2D3748",
              backgroundColor: "rgba(254,215,215,1)",
              backgroundImage: `repeating-linear-gradient(
                285deg,
                rgba(254,178,178,1) 0px,
                rgba(254,178,178,1) 1px,
                transparent 0px,
                transparent 10px
              )`,
              borderTop: "1.5px dashed #E53E3E",
              borderBottom: "1.5px dashed #E53E3E",
              borderRight: unusedIsRightEnd ? "1.5px dashed #E53E3E" : "none",
              borderLeft: unusedIsLeftEnd ? "1.5px dashed #E53E3E" : "none",
              borderTopRightRadius: unusedIsRightEnd ? 8 : 0,
              borderBottomRightRadius: unusedIsRightEnd ? 8 : 0,
              borderTopLeftRadius: unusedIsLeftEnd ? 8 : 0,
              borderBottomLeftRadius: unusedIsLeftEnd ? 8 : 0,
              boxSizing: "border-box",
              opacity: hoveredLabel ? 0.35 : 1,
              transition: "width 500ms ease, opacity 120ms ease",
              animation: `${dashRotate} 1s linear infinite`,
            }}
          >
            {Number.isInteger(unused) ? unused : unused.toFixed(2)}%
          </Box>
        )}
      </Box>

      {/* 🔥 Floating Tooltip */}
      {tooltip.label && (
        <Box
          sx={{
            position: "fixed",
            top: tooltip.y - 10,
            left: tooltip.x,
            transform: "translate(-50%, -100%)",
            backgroundColor: "#000",
            color: "#fff",
            fontSize: 12,
            fontFamily: "lato",
            px: 1.5,
            py: 0.75,
            borderRadius: "6px",
            pointerEvents: "none",
            zIndex: 9999,
            whiteSpace: "nowrap",
          }}
        >
          {tooltip.label}

          {/* 🔻 Arrow */}
          <Box
            sx={{
              position: "absolute",
              bottom: -6,
              left: "50%",
              transform: "translateX(-50%)",
              width: 0,
              height: 0,
              borderLeft: "6px solid transparent",
              borderRight: "6px solid transparent",
              borderTop: "6px solid #000",
            }}
          />
        </Box>
      )}
    </>
  );
};

export const BreakdownRow = ({
  color,
  label,
  sub,
  amount,
  percent,
  editMode,
  onPercentChange,
  onRequestEdit,
  isDimmed,
  children,
  target,
  setRows,
  isChild,
  isLastChild,
}) => {
  const [isEditing, setIsEditing] = React.useState(false);
  const [tempPercent, setTempPercent] = React.useState(percent);

  useEffect(() => {
    if (!editMode) {
      setIsEditing(false);
    }
  }, [editMode]);

  React.useEffect(() => {
    if (!setRows || !target) return;

    setRows((prev) =>
      prev.map((r) => {
        if (r.children && r.children.length > 0) {
          const updatedChildren = r.children.map((c) => ({
            ...c,
            amount: (Number(c.percent) / 100) * target,
          }));
          const parentPercent = updatedChildren.reduce(
            (sum, c) => sum + Number(c.percent || 0),
            0,
          );
          const parentAmount = updatedChildren.reduce(
            (sum, c) => sum + Number(c.amount || 0),
            0,
          );
          return {
            ...r,
            percent: parentPercent,
            amount: parentAmount,
            children: updatedChildren,
          };
        }
        return { ...r, amount: (Number(r.percent) / 100) * target };
      }),
    );
  }, [target, setRows]);

  const handleChildChange = (childLabel, newPercent) => {
    setRows((prev) => {
      const updated = prev.map((r) => {
        if (!r.children) return r;
        const updatedChildren = r.children.map((c) =>
          c.label === childLabel
            ? { ...c, percent: newPercent, amount: (newPercent / 100) * target }
            : c,
        );
        const parentPercent = updatedChildren.reduce(
          (sum, c) => sum + c.percent,
          0,
        );
        const parentAmount = updatedChildren.reduce(
          (sum, c) => sum + c.amount,
          0,
        );
        return {
          ...r,
          percent: parentPercent,
          amount: parentAmount,
          children: updatedChildren,
        };
      });

      // Recalculate profit after updating parent row
      const others = updated.filter((r) => r.label !== "Profit");
      const totalPercent = others.reduce((sum, r) => sum + (r.percent || 0), 0);
      const totalAmount = others.reduce((sum, r) => sum + (r.amount || 0), 0);

      const profitPercent = Math.max(0, 100 - totalPercent);
      const profitAmount = Math.max(0, target - totalAmount);

      return updated.map((r) =>
        r.label === "Profit"
          ? {
              ...r,
              percent: profitPercent,
              amount: profitAmount,
            }
          : r,
      );
    });
  };

  const handleEditClick = () => {
    onRequestEdit?.(() => {
      setTempPercent(percent);
      setIsEditing(true);
    });
  };
  const handleCancel = () => {
    setTempPercent(percent);
    setIsEditing(false);
  };
  const handleConfirm = () => {
    let v = tempPercent;
    if (v === "" || !Number.isFinite(Number(v))) v = 0;
    onPercentChange?.(Number(v));
    setIsEditing(false);
  };

  const canEdit = !children || children.length === 0;

  return (
    <>
      <TableRow
        sx={{ opacity: isDimmed ? 0.35 : 1, transition: "opacity 120ms ease" }}
      >
        <TableCell
          sx={{
            borderBottom:
              (isChild && !isLastChild) || (children && editMode)
                ? "none"
                : "1px solid #eee",
            height: isChild ? "40px" : "60px",

            p: isLastChild ? "8px 4px" : "14px 4px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              pl: isChild ? "2px" : 0,
              minWidth: 0,
              overflow: "hidden",
            }}
          >
            <ColorDot color={color} />
            <Box
              sx={{
                display: "flex",
                spacing: "2px",
                flexDirection: { sm: "column", md: "row" },
                minWidth: 0,
                overflow: "hidden",
              }}
            >
              <Typography
                noWrap
                sx={{
                  fontWeight: 500,
                  fontSize: isChild ? 15 : 16,
                  textOverflow: "ellipsis",
                  minWidth: 0,
                }}
              >
                {label}
              </Typography>

              {!isChild && sub && (
                <Typography
                  noWrap
                  sx={{
                    fontSize: 13,
                    color: "text.secondary",
                    mt: "3px",
                    textOverflow: "ellipsis",
                    minWidth: 0,
                    ml: { sm: "0px", md: "5px" },
                  }}
                >
                  {sub}
                </Typography>
              )}
            </Box>
          </Box>
        </TableCell>

        <TableCell
          align="left"
          sx={{
            borderBottom:
              (isChild && !isLastChild) || (children && editMode)
                ? "none"
                : "1px solid #eee",
            width: "120px",
            p: isChild ? "8px 4px" : "14px 4px",
          }}
        >
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: isChild ? 15 : 16,
              fontFamily: "lato",
            }}
          >
            {money(Math.ceil(amount))}
          </Typography>
        </TableCell>

        <TableCell
          align="right"
          sx={{
            borderBottom:
              (isChild && !isLastChild) || (children && editMode)
                ? "none"
                : "1px solid #eee",
            width: "80px",
            p: isChild ? "8px 4px" : "14px 4px",
          }}
        >
          {!isEditing ? (
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: isChild ? 15 : 16,
                fontFamily: "lato",
              }}
            >
              {Number.isInteger(percent) ? percent : percent?.toFixed(2)}%
            </Typography>
          ) : (
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                gap: 0.25,
              }}
            >
              <TextField
                type="text"
                inputMode="decimal"
                value={formatNumberWithCommasPreserveDecimal(tempPercent)}
                onChange={(e) => {
                  const next = e.target.value.replace(/,/g, "");
                  if (next === "") {
                    setTempPercent("");
                    return;
                  }

                  if (/^\d*\.?\d*$/.test(next)) {
                    setTempPercent(next);
                  }
                }}
                size="small"
                sx={{
                  width: 50,
                  "& .MuiOutlinedInput-root": {
                    height: 28,
                    borderRadius: "8px",
                    "& input": {
                      padding: 0,
                      textAlign: "center",
                      fontFamily: "lato",
                    },
                  },
                }}
              />
              <Typography sx={{ fontSize: 14, color: "text.secondary" }}>
                %
              </Typography>
            </Box>
          )}
        </TableCell>

        {editMode && (
          <TableCell
            align="right"
            sx={{
              borderBottom:
                (isChild && !isLastChild) || (children && editMode)
                  ? "none"
                  : "1px solid #eee",
              width: "60px",
              p: isChild ? "8px 4px" : "14px 4px",
            }}
          >
            {editMode && canEdit ? (
              isEditing ? (
                <Box
                  sx={{ display: "flex", gap: 0.5, justifyContent: "flex-end" }}
                >
                  <Button
                    variant="outlined"
                    size="small"
                    sx={{
                      minWidth: 28,
                      width: 28,
                      height: 28,
                      p: 0,
                      borderRadius: "8px",
                      borderColor: "#28a745",
                    }}
                    onClick={handleConfirm}
                  >
                    <CheckIcon
                      sx={{ color: "#28a745", width: 20, height: 20 }}
                    />
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    sx={{
                      minWidth: 28,
                      width: 28,
                      height: 28,
                      p: 0,
                      borderRadius: "8px",
                      borderColor: "#dc3545",
                    }}
                    onClick={handleCancel}
                  >
                    <CloseIcon
                      sx={{ color: "#dc3545", width: 20, height: 20 }}
                    />
                  </Button>
                </Box>
              ) : (
                <Button
                  variant="outlined"
                  size="small"
                  sx={{
                    minWidth: 28,
                    width: 28,
                    height: 28,
                    p: 0,
                    borderRadius: "8px",
                  }}
                  onClick={handleEditClick}
                >
                  <EditIcon sx={{ width: 20, height: 20 }} />
                </Button>
              )
            ) : (
              editMode && <Box sx={{ width: 28, height: 28 }} />
            )}
          </TableCell>
        )}
      </TableRow>
      {editMode &&
        children?.map((c, idx) => (
          <BreakdownRow
            key={c.label}
            {...c}
            editMode={editMode}
            onRequestEdit={onRequestEdit}
            onPercentChange={(v) => handleChildChange(c.label, v)}
            target={target}
            setRows={setRows}
            isChild
            isDimmed={isDimmed}
            isLastChild={idx === children.length - 1}
          />
        ))}
    </>
  );
};

export const EditableTargetCards = ({
  targets,
  targetValues,
  setTargetValues,
  customerId,
  endDate,
  setTargetValue,
  setLastUpdated,
  refreshTargets,
}) => {
  const [editModes, setEditModes] = useState(targets.map(() => false));
  const [originalValues, setOriginalValues] = useState(targetValues);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastSeverity, setToastSeverity] = useState("success");

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingSaveIndex, setPendingSaveIndex] = useState(null);
  const [changes, setChanges] = useState([]);

  const buildCardChange = (index) => {
    const previous = originalValues[index];
    const current = targetValues[index];

    if (Number(previous) === Number(current)) return null;

    return {
      label: targets[index].label,
      previous,
      newValue: current,
    };
  };

  useEffect(() => {
    setOriginalValues(targetValues);
  }, [targets]);

  useEffect(() => {
    setEditModes((prev) => {
      if (prev.length === targets.length) return prev;
      return targets.map(() => false);
    });
  }, [targets.length]);

  const handleEdit = (index) => {
    setEditModes((prev) => {
      const copy = [...prev];
      copy[index] = true;
      return copy;
    });
  };

  const handleConfirmedSave = async () => {
    const index = pendingSaveIndex;
    if (index == null) return;

    setEditModes((prev) => {
      const copy = [...prev];
      copy[index] = false;
      return copy;
    });

    const value = targetValues[index];
    const indicatorId = targets[index].id;

    try {
      await updateTargetValue({
        customerId,
        endDate,
        indicatorId,
        targetValue: value,
      });

      setOriginalValues((prev) => {
        const copy = [...prev];
        copy[index] = value;
        return copy;
      });

      setToastMessage(
        <>
          <strong>{targets[index].label} Target</strong> has been successfully
          edited
        </>,
      );
      setToastSeverity("success");
      setToastOpen(true);
      setLastUpdated?.(new Date());

      if (index === 1 && setTargetValue) {
        setTargetValue(value);
      }

      refreshTargets();
    } catch (err) {
      console.error("Failed to save target value:", err);

      setToastMessage(
        <>
          Failed to save <strong>{targets[index].label}</strong> target.
        </>,
      );
      setToastSeverity("error");
      setToastOpen(true);
    }

    setConfirmOpen(false);
    setPendingSaveIndex(null);
    setChanges([]);
  };

  const handleCancel = (index) => {
    setTargetValues(originalValues);
    setEditModes((prev) => {
      const copy = [...prev];
      copy[index] = false;
      return copy;
    });
  };

  const handleIncrement = (index) => {
    setTargetValues((prev) => {
      const copy = [...prev];
      copy[index] = Math.round(copy[index] + 100);
      return copy;
    });
  };

  const handleDecrement = (index) => {
    setTargetValues((prev) => {
      const copy = [...prev];
      copy[index] = Math.max(Math.round(copy[index] - 100), 0);
      return copy;
    });
  };

  return (
    <Box
      sx={{
        display: "grid",
        width: "100%",
        gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
        gap: 2,
        justifyItems: "stretch",
        mt: 2,
      }}
    >
      <ChangeConfirmationModal
        open={confirmOpen}
        title="Confirm Target Changes"
        description="Review your target allocation changes before saving"
        valueType="currency"
        changes={changes}
        onConfirm={handleConfirmedSave}
        onCancel={() => {
          setConfirmOpen(false);
          setPendingSaveIndex(null);
        }}
      />
      <Snackbar
        open={toastOpen}
        autoHideDuration={4000}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setToastOpen(false)}
          severity={toastSeverity}
          sx={{ width: "100%" }}
        >
          {toastMessage}
        </Alert>
      </Snackbar>
      {targets.map((option, index) => {
        const deltaValue = parseFloat(option.delta) || 0;
        const isPositive = deltaValue >= 0;

        return (
          <Paper
            key={option.label}
            elevation={0}
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              p: 2,
              borderRadius: "12px",
              textAlign: "center",
              border:
                index === 0
                  ? "1px solid #E5E7EB"
                  : index === 1
                    ? "2px solid #536DEC"
                    : "1px solid #E5E7EB",
              bgcolor: index === 0 ? "#FCFDFD" : "#FFFFFF",
            }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography
                sx={{
                  fontSize: "14px",
                  color: index === 1 ? "#536DEC" : "#718096",
                  fontWeight: 600,
                  fontFamily: "lato",
                }}
              >
                {option.label}
              </Typography>

              {editModes[index] ? (
                <Box
                  sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}
                >
                  <Button
                    variant="outlined"
                    size="small"
                    sx={{
                      minWidth: 24,
                      width: 24,
                      height: 24,
                      p: 0,
                      borderRadius: "8px",
                      borderColor: "#dc3545",
                    }}
                    onClick={() => handleCancel(index)}
                  >
                    <CloseIcon
                      sx={{ color: "#dc3545", width: 18, height: 18 }}
                    />
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    sx={{
                      minWidth: 24,
                      width: 24,
                      height: 24,
                      p: 0,
                      borderRadius: "8px",
                      borderColor: "#28a745",
                    }}
                    onClick={() => {
                      const change = buildCardChange(index);

                      if (!change) {
                        handleCancel(index);
                        return;
                      }

                      setChanges([change]);
                      setPendingSaveIndex(index);
                      setConfirmOpen(true);
                    }}
                  >
                    <CheckIcon
                      sx={{ color: "#28a745", width: 18, height: 18 }}
                    />
                  </Button>
                </Box>
              ) : (
                <IconButton onClick={() => handleEdit(index)}>
                  <EditIcon
                    sx={{
                      color: index === 1 ? "#536DEC" : "#718096",
                      fontSize: "18px",
                    }}
                  />
                </IconButton>
              )}
            </Stack>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              {editModes[index] ? (
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ mt: 3 }}
                >
                  <Button
                    variant="outlined"
                    onClick={() => handleDecrement(index)}
                    sx={{
                      minWidth: 36,
                      width: 36,
                      height: 36,
                      p: 0,
                      borderRadius: "8px",
                      borderColor: "divider",
                      fontSize: 18,
                      lineHeight: 1,
                      color: "black",
                    }}
                  >
                    −
                  </Button>

                  <TextField
                    size="small"
                    value={formatNumberWithCommasPreserveDecimal(targetValues[index])}
                    onChange={(e) => {
                      const val = e.target.value.replace(/,/g, "");
                      if (val === "") {
                        setTargetValues((prev) => {
                          const copy = [...prev];
                          copy[index] = 0;
                          return copy;
                        });
                        return;
                      }
                      if (/^\d*\.?\d{0,2}$/.test(val)) {
                        setTargetValues((prev) => {
                          const copy = [...prev];
                          copy[index] = val;
                          return copy;
                        });
                      }
                    }}
                    onBlur={() => {
                      setTargetValues((prev) => {
                        const copy = [...prev];
                        if (copy[index] === "" || copy[index] === null || copy[index] === undefined) {
                          copy[index] = 0;
                        } else {
                          copy[index] = Number(copy[index]);
                        }
                        return copy;
                      });
                    }}
                    inputProps={{
                      inputMode: "decimal",
                      pattern: "^\\d*(\\.\\d{0,2})?$",
                      style: { textAlign: "center" },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "6px",
                        display: "flex",
                        alignItems: "center",
                      },
                      flexGrow: 1,
                    }}
                  />

                  <Button
                    variant="outlined"
                    onClick={() => handleIncrement(index)}
                    sx={{
                      minWidth: 36,
                      width: 36,
                      height: 36,
                      p: 0,
                      borderRadius: "8px",
                      borderColor: "divider",
                      fontSize: 18,
                      lineHeight: 1,
                      color: "black",
                    }}
                  >
                    +
                  </Button>
                </Stack>
              ) : (
                <Typography sx={{ fontSize: 26, fontWeight: 600 }}>
                  ${Math.ceil(targetValues[index]).toLocaleString()}
                </Typography>
              )}
            </Box>

            {!editModes[index] && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  mt: 0.5,
                  gap: 0.5,
                }}
              >
                {isPositive ? (
                  <TrendingUpIcon
                    sx={{
                      fontSize: 14,
                      color: index === 1 ? "#536DEC" : "#718096",
                    }}
                  />
                ) : (
                  <TrendingDownIcon
                    sx={{
                      fontSize: 14,
                      color: index === 1 ? "#536DEC" : "#718096",
                    }}
                  />
                )}
                <Typography
                  sx={{
                    fontSize: 14,
                    fontWeight: 400,
                    color: index === 1 ? "#536DEC" : "#718096",
                  }}
                >
                  {`${option.delta}`}
                </Typography>
              </Box>
            )}
          </Paper>
        );
      })}
    </Box>
  );
};

// ------------------- MONTHLY TARGET CARD -------------------
export const MonthlyTargetCard = ({
  customerId,
  hasData,
  endDate,
  onOpenMonthly,
  onOpenCalc,
  setTargetValue,
  targetValue,
  setPreviousTargetValue,
  nextTargetValue,
  setNextTargetValue,
  setLastUpdated,
}) => {
  const [targets, setTargets] = useState([]);
  const [targetValues, setTargetValues] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!customerId) return;
    if (!hasData) return;

    const fetchTargets = async () => {
      try {
        const res = await getTargetValues({ customerId, endDate });
        const mappedTargets = ["previous", "current", "next"]
          .map((key, idx) => res[key])
          .filter(Boolean)
          .map((ind, idx) => ({
            id: ind.id,
            label:
              idx === 0
                ? "Previous Month"
                : idx === 1
                  ? "Current Month"
                  : "Next Month",
            value: ind.income_forecast,
            delta: ind.delta_pct ? `${ind.delta_pct}% vs 6-month avg` : "0%",
          }));

        setTargets(mappedTargets);
        setTargetValues(mappedTargets.map((t) => t.value));
        setPreviousTargetValue(mappedTargets[0].value);
        setNextTargetValue(mappedTargets[2].value)
      } catch (err) {
        console.error("Failed to fetch targets", err);
      }
    };
    fetchTargets();
  }, [customerId, endDate, refreshKey, targetValue, nextTargetValue, hasData]);

  return (
    <Paper
      sx={{
        borderRadius: "12px",
        minHeight: "218px",
        border: "1px solid #F7FAFC",
        backgroundColor: "white",
        boxShadow: "0px 1px 3px 0px #0000001A",
      }}
      elevation={0}
    >
      {/* Header */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        sx={{ p: { xs: "12px 16px", sm: "15px 20px" }, gap: { xs: 1, sm: 0 } }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            sx={{
              width: { xs: 36, sm: 40 },
              height: { xs: 36, sm: 40 },
              p: { xs: "8px", sm: "10px" },
              borderRadius: "8px",
              background:
                "linear-gradient(180deg, #536DEC 0%, #171923 123.75%)",
            }}
          >
            <Box
              component="img"
              src={"/white-target-icon.svg"}
              alt=""
              sx={{ width: { xs: 16, sm: 20 }, height: { xs: 16, sm: 20 } }}
            />
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography
              sx={{
                fontWeight: 700,
                fontFamily: "lato",
                fontSize: { xs: "15px", sm: "17px" },
              }}
            >
              Set Monthly Income Target
            </Typography>
            <Typography
              sx={{ fontFamily: "lato", fontSize: { xs: "12px", sm: "14px" } }}
              color="text.secondary"
            >
              View and adjust your monthly revenue goals.
            </Typography>
          </Box>
        </Box>

        {/* Buttons */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          sx={{ mt: { xs: 1, sm: 0 } }}
        >
          <Button
            variant="outlined"
            size="small"
            onClick={onOpenMonthly}
            startIcon={
              <Box
                component="img"
                src="/blue-target.svg"
                sx={{ width: 14, height: 14 }}
              />
            }
            disabled={!hasData}
            sx={{
              fontWeight: 600,
              fontSize: "14px",
              borderRadius: "8px",
              borderColor: "#627CFB",
              color: "#627CFB",
              fontFamily: "lato",
              textTransform: "none",
              px: "10px",
              "&:hover": { borderColor: "#4F64E9", bgcolor: "#627CFB0F" },
            }}
          >
            Target Cycle
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={onOpenCalc}
            startIcon={
              <Box
                component="img"
                src="/purple-calculator.svg"
                sx={{ width: 14, height: 16 }}
              />
            }
            disabled={!hasData}
            sx={{
              fontWeight: 600,
              fontSize: "14px",
              borderRadius: "8px",
              borderColor: "#627CFB",
              color: "#627CFB",
              textTransform: "none",
              fontFamily: "lato",
              px: "10px",
              "&:hover": { borderColor: "#4F64E9", bgcolor: "#627CFB0F" },
            }}
          >
            Treatment Calculator
          </Button>
        </Stack>
      </Stack>

      <Divider />

      {/* Editable target cards */}
      <Box sx={{ px: "20px", pb: "20px" }}>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            justifyContent: { xs: "center", sm: "flex-start" },
          }}
        >
          <EditableTargetCards
            targets={targets}
            targetValues={targetValues}
            setTargetValues={setTargetValues}
            customerId={customerId}
            endDate={endDate}
            currentMonthValue={targetValue}
            setTargetValue={setTargetValue}
            setLastUpdated={setLastUpdated}
            refreshTargets={() => setRefreshKey((p) => p + 1)}
          />
        </Box>
      </Box>
    </Paper>
  );
};

export const PerformanceCard = ({ performanceData }) => (
  <Paper
    sx={{
      borderRadius: "12px",
      minHeight: { xs: "240px", sm: "218px" },
      height: "100%",
      display: "flex",
      flexDirection: "column",
      border: "1px solid #F7FAFC",
      backgroundColor: "white",
      boxShadow: "0px 1px 3px 0px #0000001A",
    }}
    elevation={0}
  >
    <Box sx={{ p: { xs: "12px 16px", sm: "16px 20px" } }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box
          sx={{
            width: { xs: 28, sm: 32 },
            height: { xs: 28, sm: 32 },
            p: { xs: "6px", sm: "8px" },
            borderRadius: "8px",
            background: "linear-gradient(180deg, #536DEC 0%, #171923 123.75%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            component="img"
            src={"/white-bar-chart-icon.svg"}
            alt=""
            sx={{ width: { xs: 12, sm: 15 }, height: { xs: 12, sm: 15 } }}
          />
        </Box>

        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 700,
            fontSize: { xs: "15px", sm: "17px" },
            fontFamily: "lato",
          }}
        >
          Your Performance
        </Typography>
      </Box>
    </Box>

    <Divider />

    <Box sx={{ p: { xs: "12px 16px", sm: "16px 20px" }, flexGrow: 0 }}>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ fontSize: { xs: "12px", sm: "14px" }, fontFamily: "lato" }}
      >
        Last 6 Months Average
      </Typography>
      <Box display="flex" alignItems="center" gap="8px">
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: { xs: "22px", sm: "26px" },
            fontFamily: "lato",
          }}
        >
          {money(performanceData?.previous_avg)}
        </Typography>

        <Typography
          sx={{
            fontSize: { xs: "12px", sm: "14px" },
            mt: { xs: "10px", sm: "8px" },
            color: "#6B7280",
            fontFamily: "lato",
            whiteSpace: "nowrap",
          }}
        >
          based on actual revenue
        </Typography>
      </Box>
    </Box>

    <Box
      sx={{
        width: "100%",
        flexGrow: 1,
        minHeight: { xs: 50, sm: 60 },
        "& svg:focus, g:focus, path:focus": {
          outline: "none !important",
        },
      }}
    >
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart
          data={performanceData?.trend}
          margin={{ top: 6, right: 0, left: 0, bottom: 6 }}
        >
          <defs>
            <linearGradient id="sparklineGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="rgba(99, 91, 255, 0.2)" />
              <stop offset="100%" stopColor="rgba(99, 91, 255, 0)" />
            </linearGradient>
          </defs>

          <Area
            type="monotone"
            dataKey="value"
            stroke="none"
            fill="url(#sparklineGradient)"
            baseValue="dataMin"
            isAnimationActive={false}
          />

          <Line
            type="monotone"
            dataKey="value"
            stroke="#635BFF"
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 6, strokeWidth: 2 }}
            isAnimationActive={false}
          />

          <Tooltip
            cursor={{
              stroke: "#635BFF",
              strokeDasharray: "4 4",
              strokeWidth: 1,
            }}
            content={({ active, payload }) => {
              if (!active || !payload?.length) return null;
              const { month, value } = payload[0].payload;
              return (
                <Box
                  sx={{
                    bgcolor: "white",
                    px: "10px",
                    py: "5px",
                    borderRadius: "6px",
                    boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                    fontSize: { xs: "10px", sm: "12px" },
                    fontWeight: 500,
                    fontFamily: "lato",
                  }}
                >
                  {month}: {customCurrencyFormat(Math.ceil(value))}
                </Box>
              );
            }}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </Box>
  </Paper>
);

export const PLBreakdownCard = ({
  target,
  hasData,
  rows = [],
  setRows,
  editMode,
  onRequestEdit,
  onEdit,
  onCancel,
  setEditConfirmationOpen,
  validatePLTotals,
  setPlError,
  setPlErrorMessage,
  changes,
}) => {
  const [hoveredLabel, setHoveredLabel] = React.useState(null);
  const handlePercentChange = (label, newPercent) => {
    const clean = Number(newPercent);
    const safe = Number.isFinite(clean) ? clean : 0;

    setRows((prev) => {
      const updated = prev.map((r) =>
        r.label === label
          ? {
              ...r,
              percent: safe,
              amount: Math.ceil((safe / 100) * target),
            }
          : r,
      );

      const others = updated.filter((r) => r.label !== "Profit");

      const totalPercent = others.reduce((sum, r) => sum + (r.percent || 0), 0);
      const totalAmount = others.reduce((sum, r) => sum + (r.amount || 0), 0);

      const profitPercent = Math.max(0, 100 - totalPercent);
      const profitAmount = Math.max(0, target - totalAmount);

      return updated.map((r) =>
        r.label === "Profit"
          ? {
              ...r,
              percent: profitPercent,
              amount: profitAmount,
            }
          : r,
      );
    });
  };

  return (
    <Paper
      sx={{
        borderRadius: "12px",
        minHeight: "400px",
        border: "1px solid #F7FAFC",
        backgroundColor: "white",
        boxShadow: "0px 1px 3px 0px #0000001A",
      }}
      elevation={0}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ p: "15px 20px" }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            sx={{
              width: "40px",
              height: "40px",
              borderRadius: "8px",
              p: "10px",
              display: "flex",
              alignItems: "center",
              background:
                "linear-gradient(180deg, #536DEC 0%, #171923 123.75%)",
            }}
          >
            {" "}
            <Box
              component="img"
              src={"/pie-chart-icon.svg"}
              alt=""
              sx={{
                width: 20,
                height: 20,
              }}
            />{" "}
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 700, fontSize: "17px", fontFamily: "lato" }}
            >
              P&L Breakdown
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: "14px", fontFamily: "lato" }}
            >
              How your target should be allocated
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Box textAlign="right">
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: "13px", fontFamily: "lato" }}
            >
              Target
            </Typography>

            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 700, fontSize: "22px", fontFamily: "lato" }}
            >
              {money(target)}
            </Typography>
          </Box>

          {editMode ? (
            <>
              <Button
                variant="outlined"
                color="primary"
                sx={{
                  borderRadius: "8px",
                  width: "88px",
                  height: "40px",
                  fontFamily: "lato",
                }}
                onClick={onCancel}
              >
                Cancel
              </Button>

              <Button
                variant="contained"
                color="primary"
                sx={{
                  borderRadius: "8px",
                  width: "88px",
                  height: "40px",
                  fontFamily: "lato",
                }}
                onClick={() => {
                  if (changes.length === 0) {
                    onCancel();
                    return;
                  }

                  if (!validatePLTotals()) {
                    setPlErrorMessage(
                      <>
                        <strong>Fix needed:</strong> Your P&amp;L percentages
                        currently exceed 100% — they need to be below 100% in
                        order to save.
                      </>,
                    );
                    setPlError(true);
                    return;
                  }
                  setEditConfirmationOpen(true);
                }}
              >
                Apply
              </Button>
            </>
          ) : (
            <Button
              variant="contained"
              color="primary"
              disabled={!hasData}
              sx={{
                borderRadius: "8px",
                width: "88px",
                height: "40px",
                fontFamily: "lato",
              }}
              startIcon={<EditIcon />}
              onClick={onEdit}
            >
              Edit
            </Button>
          )}
        </Box>
      </Stack>

      <Divider></Divider>
      <Box sx={{ p: "24px 20px" }}>
        <Box>
          <SegmentedBar
            segments={rows.map((r) => ({
              label: r.label,
              value: r.percent,
              color: r.segColor,
            }))}
            hoveredLabel={hoveredLabel}
            onHover={setHoveredLabel}
          />
        </Box>

        <Box sx={{ pt: "24px", mx: "12px", gap: "12px" }}>
          <TableContainer component={Box}>
            <Table>
              <TableBody>
                {rows
                  .filter((r) => r.label !== "Profit")
                  .map((r) => (
                    <BreakdownRow
                      key={r.label}
                      {...r}
                      editMode={editMode}
                      onRequestEdit={onRequestEdit}
                      onPercentChange={(newPercent) =>
                        handlePercentChange(r.label, newPercent)
                      }
                      amount={r.amount}
                      isDimmed={hoveredLabel && hoveredLabel !== r.label}
                      isHighlighted={hoveredLabel === r.label}
                      children={r?.children}
                      target={target}
                      setRows={setRows}
                      parentLabel={r.label}
                    />
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>
    </Paper>
  );
};

export default function TargetsPage() {
  const { profile } = useCurrentProfile();

  const [activeCustomer, setActiveCustomer] = useState(null);
  const customer = activeCustomer;
  const customerId = activeCustomer?.id;

  // ======================
  // TARGET STATE
  // ======================
  const [targetValue, setTargetValue] = useState(0);
  const [previousTargetValue, setPreviousTargetValue] = useState(0);
  const [nextTargetValue, setNextTargetValue] = useState(0);

  const [rows, setRows] = useState([]);
  const [initialRows, setInitialRows] = useState([]);
  const [initialTargetValue, setInitialTargetValue] = useState(0);

  // ======================
  // DATA
  // ======================
  const [targets, setTargets] = useState({});
  const [performanceData, setPerformanceData] = useState(null);
  const [lastUpdated, setLastUpdated] = useState("");
  const [hasData, setHasData] = useState(false);

  // ======================
  // UI STATE
  // ======================
  const [editMode, setEditMode] = useState(false);
  const [editConfirmAccepted, setEditConfirmAccepted] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingEdit, setPendingEdit] = useState(null);

  const [openCalc, setOpenCalc] = useState(false);
  const [monthlyOpen, setMonthlyOpen] = useState(false);

  const [plError, setPlError] = useState(false);
  const [plErrorMessage, setPlErrorMessage] = useState();

  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastSeverity, setToastSeverity] = useState("success");

  const [expanded, setExpanded] = useState(true);
  const [showSuccess, setShowSuccess] = useState(false);
  const [editConfirmationOpen, setEditConfirmationOpen] = useState(false);

  // ======================
  // DERIVED VALUES
  // ======================
  const date = customer?.dashboard_date
    ? new Date(endOfMonth(parseISO(customer.dashboard_date)).toDateString())
    : fallbackDate;

  const formattedDate = format(date, "yyyy-MM-dd");

  const isMobile = useMediaQuery("(max-width:1280px)");

  // ======================
  // CUSTOMER LOAD
  // ======================
  useEffect(() => {
    if (!profile) return;

    const storedId = sessionStorage.getItem("selectedCustomerId");
    if (!storedId) return;

    if (profile.type !== "admin") {
      const customer =
        profile.customers.find((c) => c.id.toString() === storedId) ||
        profile.customers[0] ||
        null;
      setActiveCustomer(customer);
      return;
    }

    const fetchCustomer = async () => {
      try {
        const res = await axios.get(
          `/api/v2/customers/${storedId}/`,
          { headers: { "Content-Type": "application/json" } },
          { withCredentials: true },
        );
        setActiveCustomer(res.data);
      } catch (err) {
        console.error("Error fetching admin customer:", err);
      }
    };

    fetchCustomer();
  }, [profile]);

  useEffect(() => {
    const handleSelectedChange = async () => {
      const storedId = sessionStorage.getItem("selectedCustomerId");
      if (!storedId) return;

      if (profile.type !== "admin") {
        const found = profile.customers.find(
          (c) => c.id.toString() === storedId,
        );
        if (found) setActiveCustomer(found);
      } else {
        try {
          const res = await axios.get(`/api/v2/customers/${storedId}/`, {
            headers: { "Content-Type": "application/json" },
            withCredentials: true,
          });
          setActiveCustomer(res.data);
        } catch (err) {
          console.error("Error fetching admin customer:", err);
        }
      }
    };

    window.addEventListener("selectedCustomerId-changed", handleSelectedChange);
    return () => {
      window.removeEventListener(
        "selectedCustomerId-changed",
        handleSelectedChange,
      );
    };
  }, [profile]);

  // ======================
  // DATA FETCHING
  // ======================
  useEffect(() => {
  if (!customerId) return;

  getCustomerMonthlyTargets(customerId, formattedDate)
    .then((res) => {
      setTargets(res[0]);
      setHasData(true);
    })
    .catch((err) => {
      console.log(err);
      setHasData(false);
    });
}, [customerId, formattedDate]);

  useEffect(() => {
    if (!customerId) return;
    if (!hasData) return;

    const controller = new AbortController();

    getCustomTargetPerformanceData(customerId, controller.signal, formattedDate)
      .then((res) => {
        setPerformanceData(res);
        setLastUpdated(res.last_targets_updated_at);
      })
      .catch((err) => {
        if (err.name !== "CanceledError") {
          console.error("Failed to fetch custom target performance", err);
        }
      });

    return () => {
      controller.abort();
    };
  }, [customerId, formattedDate, hasData]);

  // ======================
  // ROW INIT
  // ======================
  useEffect(() => {
    if (!targets) return;

    const toNum = (v) => (v == null ? 0 : Number(v));

    const nextTargetValue = toNum(targets?.income_forecast);

    const nextRows = [
      {
        color: "#00C950",
        segColor: "#38A169",
        label: "Profit",
        sub: "Your take-home",
        amount: toNum((nextTargetValue * Number(targets.profit_pl)) / 100) || 0,
        percent: toNum(targets.profit_pl),
      },
      {
        color: "#EF954B",
        segColor: "#ED8936",
        label: "Team Costs",
        sub: "Salaries & contractors",
        amount: toNum(targets.team_forecast),
        percent: toNum(targets.team_pl),
      },
      {
        color: "#4CBAB5",
        segColor: "#38B2AC",
        label: "Operations",
        sub: "Practice cost, overhead & business development",
        amount:
          toNum(targets.practice_cost_forecast) +
          toNum(targets.overhead_forecast) +
          toNum(targets.business_development_forecast),
        percent:
          toNum(targets.practice_cost_pl) +
          toNum(targets.overhead_pl) +
          toNum(targets.business_development_pl),
        children: [
          {
            label: "Practice Costs",
            sub: "Rent, utilities, software",
            amount: toNum(targets.practice_cost_forecast),
            percent: toNum(targets.practice_cost_pl),
          },
          {
            label: "Overhead",
            sub: "Admin & ops",
            amount: toNum(targets.overhead_forecast),
            percent: toNum(targets.overhead_pl),
          },
          {
            label: "Business Development",
            sub: "Partnerships & expansion",
            amount: toNum(targets.business_development_forecast),
            percent: toNum(targets.business_development_pl),
          },
        ],
      },
      {
        color: "#728AFC",
        segColor: "#627CFB",
        label: "Marketing",
        sub: "Ads & promotions",
        amount: toNum(targets.marketing_forecast),
        percent: toNum(targets.marketing_pl),
      },
      {
        color: "#EF74AF",
        segColor: "#ED64A6",
        label: "Products",
        sub: "Inventory & supplies",
        amount: toNum(targets.cogs_forecast),
        percent: toNum(targets.cogs_pl),
      },
    ];

    setTargetValue(Math.ceil(nextTargetValue));
    setRows(nextRows);

    setInitialTargetValue(Math.ceil(nextTargetValue));
    setInitialRows(nextRows);
  }, [targets]);

  // ======================
  // ROW RECALC
  // ======================
  useEffect(() => {
    setRows((prev) =>
      prev.map((r) => ({
        ...r,
        amount: Math.round((r.percent / 100) * targetValue),
      })),
    );
  }, [targetValue]);

  // ======================
  // UI HANDLERS
  // ======================
  const handleEditClick = () => {
    setEditMode(true);
    setConfirmOpen(true);
  };

  const handleCancelClick = () => {
    setTargetValue(Math.ceil(initialTargetValue));
    setRows(initialRows);
    setEditMode(false);
  };

  const requestRowEdit = (proceed) => {
    if (editConfirmAccepted) {
      proceed();
    } else {
      setPendingEdit(() => proceed);
      setConfirmOpen(true);
    }
  };

  const handleOpenCalc = () => setOpenCalc(true);
  const handleCloseCalc = () => setOpenCalc(false);

  // ======================
  // VALIDATION
  // ======================
  const validatePLTotals = () => {
    const total = rows.reduce((sum, row) => {
      return sum + (Number(row.percent) || 0);
    }, 0);

    return total === 100;
  };

  // ======================
  // SAVE
  // ======================
  const handleSaveTargets = async () => {
    if (!customerId) return;

    const payload = {};

    rows.forEach((r) => {
      switch (r.label) {
        case "Profit":
          payload.profit_pl = r.percent;
          payload.income_forecast = targetValue;
          break;

        case "Team Costs":
          payload.team_pl = r.percent;
          payload.team_forecast = r.amount;
          break;

        case "Operations":
          if (r.children?.length) {
            r.children.forEach((c) => {
              switch (c.label) {
                case "Practice Costs":
                  payload.practice_cost_pl = c.percent;
                  payload.practice_cost_forecast = c.amount;
                  break;
                case "Overhead":
                  payload.overhead_pl = c.percent;
                  payload.overhead_forecast = c.amount;
                  break;
                case "Business Development":
                  payload.business_development_pl = c.percent;
                  payload.business_development_forecast = c.amount;
                  break;
              }
            });
          }
          break;

        case "Marketing":
          payload.marketing_pl = r.percent;
          payload.marketing_forecast = r.amount;
          break;

        case "Products":
          payload.cogs_pl = r.percent;
          payload.cogs_forecast = r.amount;
          break;
      }
    });

    try {
      await saveCustomMonthlyTarget(
        customerId,
        date.getFullYear(),
        date.getMonth() + 1,
        payload,
      );

      setInitialTargetValue(targetValue);
      setInitialRows(rows);
      setEditMode(false);

      setToastMessage(
        <>
          <strong>P&amp;L Breakdown</strong> has successfully been edited
        </>,
      );
      setToastSeverity("success");
      setToastOpen(true);

      console.log("Targets saved successfully!");
    } catch (err) {
      setPlErrorMessage("Failed to save.");
      console.error("Failed to save targets:", err);

      setToastMessage("Failed to save P&L Breakdown.");
      setToastSeverity("error");
      setToastOpen(true);
    }

    getCustomTargetPerformanceData(customerId, "", formattedDate)
      .then((res) => {
        setPerformanceData(res);
      })
      .catch((err) => {
        if (err.name !== "CanceledError") {
          console.error("Failed to fetch custom target performance", err);
        }
      });
  };

  // ======================
  // SIDEBAR
  // ======================
  useEffect(() => {
    const handleSidebarExpand = () => setExpanded(true);
    const handleSidebarCollapse = () => setExpanded(false);

    window.addEventListener("sidebar:expand", handleSidebarExpand);
    window.addEventListener("sidebar:collapse", handleSidebarCollapse);

    return () => {
      window.removeEventListener("sidebar:expand", handleSidebarExpand);
      window.removeEventListener("sidebar:collapse", handleSidebarCollapse);
    };
  }, []);

  // ======================
  // RESIZE OBSERVER
  // ======================
  const plErrorRef = useRef(null);
  const [plErrorHeight, setPlErrorHeight] = useState(0);

  useEffect(() => {
    if (!plErrorRef.current) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setPlErrorHeight(entry.contentRect.height);
      }
    });

    observer.observe(plErrorRef.current);

    return () => observer.disconnect();
  }, [plError]);

  // ======================
  // CHANGES
  // ======================
  const buildChanges = (initialRows, currentRows) => {
    const changes = [];

    const mapRows = (rows, parentLabel = null) => {
      const map = {};

      rows.forEach((r) => {
        const key = parentLabel ? `${parentLabel} > ${r.label}` : r.label;
        map[key] = r.percent;

        if (r.children?.length) {
          Object.assign(map, mapRows(r.children, r.label));
        }
      });

      return map;
    };

    const initialMap = mapRows(initialRows);
    const currentMap = mapRows(currentRows);

    Object.keys(currentMap).forEach((key) => {
      const prev = initialMap[key] ?? 0;
      const curr = currentMap[key] ?? 0;

      if (Number(prev) !== Number(curr)) {
        changes.push({
          label: key,
          previous: prev,
          newValue: curr,
        });
      }
    });

    return changes;
  };

  const changes = buildChanges(initialRows, rows);

  return (
    <ClientDashboardLayout>
      <Box
        sx={{
          bgcolor: "#F9FAFB",
          minHeight: "100vh",
          fontFamily: "lato",
        }}
      >
        <Snackbar
          open={toastOpen}
          autoHideDuration={4000}
          onClose={() => setToastOpen(false)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            onClose={() => setToastOpen(false)}
            severity={toastSeverity}
            sx={{ width: "100%" }}
          >
            {toastMessage}
          </Alert>
        </Snackbar>
        <SuccessTargetModal
          open={showSuccess}
          onClose={() => setShowSuccess(false)}
          monthlyTarget={formatMoney(targetValue)}
          profitGoal={formatMoney(rows[0]?.amount)}
        />
        <ChangeConfirmationModal
          open={editConfirmationOpen}
          title="Confirm Target Changes"
          description="Review your P&L allocation changes before saving"
          changes={changes}
          onCancel={() => setEditConfirmationOpen(false)}
          onConfirm={() => {
            handleSaveTargets();
            setEditConfirmationOpen(false);
          }}
        />

        <Dialog
          open={confirmOpen}
          onClose={() => {
            setConfirmOpen(false);
            setEditMode(false);
          }}
          PaperProps={{
            sx: {
              borderRadius: "12px",
              p: "36px 32px",
              maxWidth: 600,
              textAlign: "left",
            },
          }}
        >
          <DialogTitle
            sx={{
              p: 0,
              mb: 1,
              fontWeight: 700,
              fontSize: "26x",
              fontFamily: "lato",
            }}
          >
            Important Note
          </DialogTitle>

          <DialogContent sx={{ p: 0 }}>
            <Typography
              variant="body2"
              color="rgba(74, 85, 104, 1)"
              fontSize={"16px"}
              sx={{ fontFamily: "lato" }}
            >
              You are editing your custom targets.
              <br />
              <br />
              These changes will stay in effect until they are updated again.
              Updating income or Perfect P&L targets will change the benchmarks
              used to measure performance across the platform, which may affect
              how your financial results are viewed and interpreted.
              <br />
              <br />
              Default targets cannot be edited here. To update default targets,
              please contact your Lucro Account Manager directly.
            </Typography>
          </DialogContent>

          <DialogActions
            sx={{
              p: 0,
              mt: 3,
              display: "flex",
              justifyContent: "center",
              gap: 1,
            }}
          >
            <Button
              variant="outlined"
              onClick={() => {
                setConfirmOpen(false);
                setEditMode(false);
              }}
              sx={{
                height: "48px",
                width: "100%",
                maxWidth: "258px",
                fontFamily: "lato",
                borderRadius: "8px",
              }}
            >
              Cancel For Now
            </Button>

            <Button
              variant="contained"
              onClick={() => {
                setEditConfirmAccepted(true);
                setConfirmOpen(false);
                pendingEdit?.();
                setPendingEdit(null);
              }}
              sx={{
                height: "48px",
                width: "100%",
                maxWidth: "258px",
                fontFamily: "lato",
                borderRadius: "8px",
              }}
            >
              Continue Editing
            </Button>
          </DialogActions>
        </Dialog>

        <Box>
          {isMobile && (
            <Card
              elevation={0}
              sx={{
                position: "fixed",
                top: 0,
                left: 0,
                zIndex: 999,
                borderRadius: 0,
                height: "72px",
                width: "100%",
                borderBottom: "1px solid #e0e0e0",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  height: "100%",
                  width: "100%",
                  px: 2,
                }}
              >
                <Button
                  variant="contained"
                  disableElevation
                  sx={{
                    backgroundColor: "white",
                    width: 50,
                    height: 50,
                    p: 1,
                    minWidth: 50,
                    "&:hover": { backgroundColor: "white" },
                    "&:active": { backgroundColor: "white" },
                  }}
                  onClick={() => {
                    window.dispatchEvent(new Event("sidebar:expand"));
                  }}
                >
                  <MenuIcon style={{ width: 40, height: 40, color: "black" }} />
                </Button>
              </Box>
            </Card>
          )}
          <Paper
            elevation={0}
            sx={{
              p: isMobile ? "8px" : 2,
              borderRadius: 0,
              backgroundColor: "#FFFFFF",
              boxShadow: "0px 1px 3px rgba(9, 4, 4, 0.08)",
              height: isMobile ? "52px" : "84px",
              position: "fixed",
              maxWidth: "100%",
              width: "100%",
              zIndex: 1000,
              mt: isMobile ? "72px" : "0px",
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Box>
                <Typography
                  display={isMobile ? "none" : "block"}
                  variant="h5"
                  sx={{ fontWeight: 700, fontSize: "24px", fontFamily: "lato" }}
                >
                  Targets
                </Typography>
                <Typography
                  display={isMobile ? "none" : "block"}
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontWeight: 400, fontSize: "16px", fontFamily: "lato" }}
                >
                  Start by setting an income goal. You can enter it manually, or
                  build it from your treatment mix.
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  mr: !isMobile ? (expanded === true ? "300px" : "95px") : 0,
                }}
              >
                {performanceData?.last_targets_updated_at && (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        textAlign: "right",
                        fontSize: "11px",
                        fontFamily: "lato",
                      }}
                    >
                      Last Updated
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        textAlign: "right",
                        fontSize: "12px",
                        fontFamily: "lato",
                      }}
                    >
                      {lastUpdated
                        ? (() => {
                            let dateObj;

                            if (typeof lastUpdated === "string") {
                              const parsed = new Date(lastUpdated);
                              if (isNaN(parsed.getTime()))
                                return performanceData?.last_targets_updated_at;
                              dateObj = parsed;
                            } else if (lastUpdated instanceof Date) {
                              dateObj = lastUpdated;
                            } else {
                              return performanceData?.last_targets_updated_at;
                            }

                            const year = dateObj.getFullYear();
                            const month = String(
                              dateObj.getMonth() + 1,
                            ).padStart(2, "0");
                            const day = String(dateObj.getDate()).padStart(
                              2,
                              "0",
                            );
                            const hours12 = String(
                              dateObj.getHours() % 12 || 12,
                            ).padStart(2, "0");
                            const minutes = String(
                              dateObj.getMinutes(),
                            ).padStart(2, "0");
                            const ampm = dateObj.getHours() >= 12 ? "PM" : "AM";

                            return `${year}/${month}/${day}, ${hours12}:${minutes}${ampm}`;
                          })()
                        : performanceData?.last_targets_updated_at}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Stack>
          </Paper>
          {plError && (
            <Box
              ref={plErrorRef}
              sx={{
                mt: isMobile ? "120px" : "82px",
                position: "fixed",
                width: `calc(100% - ${
                  !isMobile ? (expanded === true ? "300px" : "95px") : "0px"
                })`,
                zIndex: 500,
              }}
            >
              <Alert
                severity="error"
                icon={false}
                sx={{
                  bgcolor: "#FEE2E2",
                  color: "#991B1B",
                  borderRadius: "0px",
                  fontFamily: "lato",
                  fontSize: "14px",

                  "& .MuiAlert-message": {
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: 0,
                  },
                }}
                action={
                  <IconButton
                    size="small"
                    onClick={() => setPlError(false)}
                    sx={{ color: "#991B1B" }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                }
              >
                <Box
                  component="img"
                  src="/FiAlertTriangle.svg"
                  alt=""
                  sx={{ width: 20, height: 20 }}
                />
                <Typography sx={{ fontFamily: "lato" }}>
                  {plErrorMessage}
                </Typography>
              </Alert>
            </Box>
          )}

          <Box sx={{ p: "32px 24px" }}>
            <Box
              sx={{
                display: "grid",
                gap: 3,
                gridTemplateColumns: {
                  xs: "1fr",
                  md: "2fr 1fr",
                },
                alignItems: "stretch",
                mt: `calc(${!isMobile ? "84px" : "126px"} + ${
                  plError ? plErrorHeight : 0
                }px)`,
              }}
            >
              <Box sx={{ height: "100%" }}>
                <MonthlyTargetCard
                  target={Number(targets?.income_forecas)}
                  hasData={hasData}
                  editMode={editMode}
                  onOpenCalc={handleOpenCalc}
                  performanceData={performanceData}
                  targetValue={targetValue}
                  setTargetValue={setTargetValue}
                  onOpenMonthly={() => setMonthlyOpen(true)}
                  customerId={customerId}
                  endDate={formattedDate}
                  previousTargetValue={previousTargetValue}
                  setPreviousTargetValue={setPreviousTargetValue}
                  nextTargetValue={nextTargetValue}
                  setNextTargetValue={setNextTargetValue}
                  setLastUpdated={setLastUpdated}
                />
              </Box>

              <Box sx={{ height: "100%" }}>
                <PerformanceCard performanceData={performanceData} />
              </Box>
            </Box>

            <Box sx={{ mt: 3 }}>
              <PLBreakdownCard
                target={targetValue}
                hasData={hasData}
                rows={rows}
                setRows={setRows}
                editMode={editMode}
                onRequestEdit={requestRowEdit}
                onEdit={handleEditClick}
                onCancel={handleCancelClick}
                setEditConfirmationOpen={setEditConfirmationOpen}
                validatePLTotals={validatePLTotals}
                setPlError={setPlError}
                setPlErrorMessage={setPlErrorMessage}
                changes={changes}
              />
            </Box>
          </Box>
        </Box>
      </Box>
      <TreatmentCalculatorUI
        onClose={handleCloseCalc}
        open={openCalc}
        customerId={customerId}
        setTargetValue={setTargetValue}
        setLastUpdated={setLastUpdated}
        endDate={formattedDate}
        indicatorId={targets?.id}
      ></TreatmentCalculatorUI>

      <MonthlyTargetModal
        open={monthlyOpen}
        onClose={() => setMonthlyOpen(false)}
        targetValue={targetValue}
        customerId={customerId}
        setTargetValue={setTargetValue}
        previousTargetValue={previousTargetValue}
        formattedDate={formattedDate}
        nextTargetValue={nextTargetValue}
        setNextTargetValue={setNextTargetValue}
        setLastUpdated={setLastUpdated}
      />
    </ClientDashboardLayout>
  );
}
